/**
 * Map View Component
 *
 * Clean, focused component following Single Responsibility Principle
 * and Clean Architecture patterns
 */

import React, {useState, useEffect, useCallback, useMemo} from 'react';
import Map from 'react-map-gl';
import {MapConfigurationService} from '../../../Services/map/MapConfigurationService';
import {OfflineMap} from './OfflineMap';
import {MAP_CONFIG, MapStyleState} from '../../../constants/mapConfig';

export interface MapViewProps {
  viewState: {
    latitude: number;
    longitude: number;
    zoom: number;
  };
  onMoveStart: () => void;
  onMove: (e: any) => void;
  onMoveEnd: (e: any) => void;
  children?: React.ReactNode;
}

interface MapViewState {
  mapError: string | null;
  retryCount: number;
  mapStyleState: MapStyleState;
  isLoading: boolean;
}

export const MapView: React.FC<MapViewProps> = ({
  viewState,
  onMoveStart,
  onMove,
  onMoveEnd,
  children
}) => {
  const mapConfig = MapConfigurationService.getInstance();
  const mapProps = mapConfig.getMapProps();

  // Start on Mapbox primary when a token is configured — only fall to OSM
  // (the 'offline' style) if Mapbox itself fails. The previous default of
  // always starting on 'offline' meant we hammered tile.openstreetmap.org
  // even when Mapbox would have worked, and any OSM hiccup cascaded the
  // whole page through to 'complete-offline' (= no map at all). When no
  // token is set we still start on 'offline' so the cascade behaves the
  // same as before for tenants without a Mapbox plan.
  const initialStyleState: MapStyleState = mapConfig.isTokenValid()
    ? 'primary'
    : 'offline';

  const [state, setState] = useState<MapViewState>({
    mapError: null,
    retryCount: 0,
    mapStyleState: initialStyleState,
    isLoading: true
  });

  // Memoized map style to prevent unnecessary recalculations
  const currentMapStyle = useMemo(() => {
    switch (state.mapStyleState) {
      case 'primary':
        return mapConfig.getMapStyle();
      case 'fallback':
        return mapConfig.getFallbackMapStyle();
      case 'offline':
        return mapConfig.getOfflineMapStyle();
      default:
        return mapConfig.getMapStyle();
    }
  }, [state.mapStyleState, mapConfig]);

  // Clean error handler
  const handleMapError = useCallback((error: any) => {
    const errorMessage = error?.message || 'Map loading failed';

    setState(prevState => {
      const newRetryCount = prevState.retryCount + 1;

      if (newRetryCount >= MAP_CONFIG.MAX_RETRY_ATTEMPTS) {
        return {
          ...prevState,
          mapError: `Failed to load map after ${MAP_CONFIG.MAX_RETRY_ATTEMPTS} attempts: ${errorMessage}`,
          isLoading: false
        };
      }

      // Try next fallback style
      const nextStyleState = getNextStyleState(prevState.mapStyleState);
      return {
        ...prevState,
        mapStyleState: nextStyleState,
        retryCount: newRetryCount
      };
    });
  }, []);

  // Clean map load handler
  const handleMapLoad = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      isLoading: false,
      mapError: null
    }));
  }, []);

  // Clean retry handler
  const handleRetry = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      mapError: null,
      retryCount: 0,
      mapStyleState: 'primary',
      isLoading: true
    }));
  }, []);

  // Reset state when viewState changes
  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      mapError: null,
      isLoading: true
    }));
  }, [viewState]);

  // Handle timeout for primary map style
  useEffect(() => {
    if (state.mapStyleState === 'primary') {
      const timeout = setTimeout(() => {
        setState(prevState => ({
          ...prevState,
          mapStyleState: 'fallback'
        }));
      }, MAP_CONFIG.LOAD_TIMEOUT);

      return () => clearTimeout(timeout);
    }
  }, [state.mapStyleState]);

  // Use complete offline mode if all Mapbox styles fail
  if (state.mapStyleState === 'complete-offline') {
    return (
      <OfflineMap
        viewState={viewState}
        onMoveStart={onMoveStart}
        onMove={onMove}
        onMoveEnd={onMoveEnd}
      >
        {children}
      </OfflineMap>
    );
  }

  // Show error state
  if (state.mapError) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 rounded-lg">
        <div className="text-center p-6">
          <div className="text-red-500 text-lg mb-4">🗺️</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Map Loading Error</h3>
          <p className="text-gray-600 mb-4">{state.mapError}</p>
          <button
            onClick={handleRetry}
            className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 transition-colors"
          >
            Retry ({state.retryCount + 1})
          </button>
        </div>
      </div>
    );
  }

  const isOfflineMode = state.mapStyleState === 'offline';

  return (
    <div className="relative w-full h-full">
      {/* Loading indicator */}
      {state.isLoading && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg px-4 py-3 border">
          <div className="flex items-center gap-3 text-sm text-gray-700">
            <div className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="font-medium">
              {getLoadingMessage(state.mapStyleState)}
            </span>
          </div>
        </div>
      )}

      <Map
        {...viewState}
        {...mapProps}
        mapStyle={currentMapStyle as any}
        onMoveStart={onMoveStart}
        onMove={onMove}
        onMoveEnd={onMoveEnd}
        onLoad={handleMapLoad}
        style={{width: '100%', height: '100%'}}
        onError={handleMapError}
        key={`${state.retryCount}-${state.mapStyleState}`}
        attributionControl={false}
        logoPosition="bottom-left"
      >
        {isOfflineMode && (
          <div className="absolute top-2 left-2 bg-white bg-opacity-90 px-2 py-1 rounded text-xs text-gray-600 z-10">
            Offline Mode - OpenStreetMap
          </div>
        )}
        {children}
      </Map>
    </div>
  );
};

// Helper functions
function getNextStyleState (currentState: MapStyleState): MapStyleState {
  switch (currentState) {
    case 'primary':
      return 'fallback';
    case 'fallback':
      return 'offline';
    case 'offline':
      return 'complete-offline';
    default:
      return 'complete-offline';
  }
}

function getLoadingMessage (mapStyleState: MapStyleState): string {
  switch (mapStyleState) {
    case 'primary':
      return 'Loading Mapbox...';
    case 'fallback':
      return 'Loading fallback map...';
    case 'offline':
      return 'Loading offline map...';
    default:
      return 'Loading map...';
  }
}

export default MapView;
